import {partition} from 'lodash';
import {type Moment} from 'moment';
import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {createPaginationContainer, graphql, RelayPaginationProp} from 'react-relay';
import {APPROVAL_STATUS} from '../../../../../../mutations/ts/time_registrations/__generated__/DuplicateTimeRegistrationsInDateRangeMutation.graphql';
import {withSocketHandling} from '../../../../../../socket/withSocketHandling';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {TimeRegistrationTargetSuggstionsData} from '../../../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {getSocketConfig} from '../../../timesheet_socket_handling';
import {TIME_PERIOD, type TimePeriod} from '../../../timesheets_change_date_view';
import {groupRegs, addTimeRegTypes, sortList} from '../timesheet-table-util/DataUtil';
import {TimesheetsTimeRowTable as TimesheetTable} from '../TimesheetsTimeRowTable';
import type {TimesheetColumnSort} from '../../usePersistentTimesheetColumnSort';
import type {TimesheetThisWeeksWorkTableWrapper_viewer} from './__generated__/TimesheetThisWeeksWorkTableWrapper_viewer.graphql';
import {useTimesheetsTaskTableColumns} from './useTimesheetsTaskTableColumns';

type Company = NonNullable<TimesheetThisWeeksWorkTableWrapper_viewer['company']>;

type QueryTasks = NonNullable<NonNullable<Company['taskTypeQueryTasks']>['edges']>;

type QueryTaskNode = NonNullable<NonNullable<QueryTasks[0]>['node']>;

function flattenQueryTasks(queryTasks: QueryTasks): QueryTaskNode[] {
	return queryTasks.reduce((acc: QueryTaskNode[], task) => {
		if (task && task.node) {
			acc.push(task.node);
		}
		return acc;
	}, []);
}

function partitionQueryTasksWithTimeRegs(
	queryTasksFlat: QueryTaskNode[],
	timeRegsForPeriod
): [QueryTaskNode[], QueryTaskNode[]] {
	const timeRegistrationTaskIdsSet = new Set(
		timeRegsForPeriod.filter(tReg => !!tReg?.node?.task).map(tReg => tReg.node.task.id)
	);
	return partition(queryTasksFlat, task => timeRegistrationTaskIdsSet.has(task.id));
}

function useEmptyListTracking(list?: unknown[], properties?: Record<string, unknown>) {
	const trackingref = useRef(-1);
	const count = list?.length || 0;
	if (trackingref.current === -1 && count === 0) {
		trackingref.current = 0;
		trackEvent('This Weeks Work', 'Empty List', properties);
	}
}

type TimesheetThisWeeksWorkTableWrapperProps = {
	viewer: TimesheetThisWeeksWorkTableWrapper_viewer;
	relay: RelayPaginationProp;
	setSocketVariables: (totalCount: number) => void;
	setSocketConfig: (getSocketConfig, refetchFunc: RelayPaginationProp['refetchConnection']) => void;
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	timeRegistrations; // TimesheetsPage_viewer['timeRegistrations']['edges']
	timeRegsForPeriod; // TimesheetsPage_viewer['timeRegistrations']['edges']
	currentViewingDate: Moment;
	timePeriod: TimePeriod;
	filterActive: boolean;
	searchActive: boolean;
	usingCurrentUserAssigneeFilter: boolean;
	sort: TimesheetColumnSort;
	selectDate: (day: Moment) => void;
	holidays; // TimesheetsPage_viewer['company']['person']['holidayCalendar']['holidayCalendarEntries']['edges']
	lockedDate?: Moment;
	isFullyLocked: boolean;
	usingTimeApproval: boolean;
	usingInternalTimeApproval: boolean;
	periodTimeApprovalStatus: APPROVAL_STATUS;
	overBudgetProgramsByCompanyProjectId; // Custom type of {companyProjectId, program}
	timeRegistrationTargetSuggestionsData: TimeRegistrationTargetSuggstionsData;
	paginationRef;
	goToTimesheetsTab: () => void;
	refetchEntries: () => void;
	customFieldDefinitions; // TimesheetsPage_viewer['company']['customFieldDefinitions]['edges'][0]['node'];
};

const TimesheetThisWeeksWorkTableWrapper = ({
	viewer,
	relay,
	setSocketVariables,
	setSocketConfig,
	selectedPerson,
	timeRegistrations,
	timeRegsForPeriod,
	currentViewingDate,
	timePeriod,
	filterActive,
	searchActive,
	usingCurrentUserAssigneeFilter,
	sort,
	selectDate,
	holidays,
	lockedDate,
	isFullyLocked,
	usingTimeApproval,
	usingInternalTimeApproval,
	periodTimeApprovalStatus,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	paginationRef,
	goToTimesheetsTab,
	refetchEntries,
	customFieldDefinitions,
}: TimesheetThisWeeksWorkTableWrapperProps): React.ReactElement => {
	const [movedTasks, setMovedTasks] = useState([]);

	const fetching = useRef(false);

	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const columns = useTimesheetsTaskTableColumns({
		viewer,
		selectedPerson,
		isWeekView,
		customFieldDefinitions,
	});

	/* FIXME: Sorting is currently not aligned between front-end and back-end so results of loadMore will not appear at the bottom of the list */
	const loadMore = (pageSize, callback, options) => {
		relay.loadMore(pageSize, callback, options);
		const taskCount = viewer?.company?.taskTypeQueryTasks?.edges?.length || 0;
		setSocketVariables(taskCount + pageSize);
	};

	const hasNextPage = viewer?.company?.taskTypeQueryTasks?.pageInfo.hasNextPage;

	const fetchMoreTasks = () => {
		const onSuccess = () => {
			fetching.current = false;
			return;
		};
		if (hasNextPage && !fetching.current) {
			fetching.current = true;
			loadMore(30, onSuccess, {});
		}
	};

	useImperativeHandle(
		paginationRef,
		() => ({
			loadMore() {
				fetchMoreTasks();
			},
		}),
		[hasNextPage]
	);

	useEffect(() => {
		const selectedPersonIdInt = parseInt(atob(selectedPerson.id).replace('Person:', ''));
		setSocketConfig(getSocketConfig([selectedPersonIdInt]), relay.refetchConnection);
	}, [selectedPerson]);

	const queryTasks = flattenQueryTasks(viewer?.company?.taskTypeQueryTasks?.edges ?? []);
	const [queryTasksWithTimeRegistrations, queryTasksWithoutTimeRegistrations] = partitionQueryTasksWithTimeRegs(
		queryTasks,
		timeRegsForPeriod
	);

	const queryTasksWithTimeRegistrationsIds = queryTasksWithTimeRegistrations.map(task => task.id).concat(movedTasks);

	const _timeRegsMoved = timeRegsForPeriod.filter(
		tReg => tReg.node.task && queryTasksWithTimeRegistrationsIds.some(taskId => taskId === tReg.node.task.id)
	);

	useEmptyListTracking(queryTasks, {filterActive, searchActive, usingCurrentUserAssigneeFilter});

	// TODO: In an effort to standardise the timesheet table we are in the process of refactoring
	// it to take a single list of row entities, rather than an array or map, of edges or nodes.
	// It's likely that much of this complexity be be refactored away.

	const timeRegsWithTypes = addTimeRegTypes(_timeRegsMoved);

	let standardisedEntityList;
	if (isWeekView) {
		const groupedMovedTasks = [...groupRegs(timeRegsWithTypes).entries()];

		standardisedEntityList = [
			...groupedMovedTasks.map(([entity, dayRegsForEntity]) => ({
				...entity,
				dayRegs: dayRegsForEntity,
			})),
			...queryTasksWithoutTimeRegistrations.map(task => ({
				...task,
				dayRegs: [[], [], [], [], [], [], []],
			})),
		];
	} else {
		standardisedEntityList = [...timeRegsWithTypes, ...queryTasksWithoutTimeRegistrations].filter(
			e => !(e.node && e.node.task && e.node.task.project === null)
		);
	}

	const sortedList = sortList(standardisedEntityList, sort.value, sort.ascending);

	return (
		<TimesheetTable
			entityList={sortedList}
			sort={sort}
			setExternalMovedTasks={setMovedTasks}
			loading={false}
			viewer={viewer}
			selectedPerson={selectedPerson}
			columns={columns}
			timeRegistrations={timeRegistrations}
			currentViewingDate={currentViewingDate}
			timePeriod={timePeriod}
			filterActive={filterActive}
			searchActive={searchActive}
			usingCurrentUserAssigneeFilter={usingCurrentUserAssigneeFilter}
			selectDate={selectDate}
			holidays={holidays}
			lockedDate={lockedDate}
			isFullyLocked={isFullyLocked}
			usingTimeApproval={usingTimeApproval}
			usingInternalTimeApproval={usingInternalTimeApproval}
			periodTimeApprovalStatus={periodTimeApprovalStatus}
			overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
			timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
			goToTimesheetsTab={goToTimesheetsTab}
			thisWeekWork={true}
			refetchEntries={refetchEntries}
		/>
	);
};

const TimesheetThisWeeksWorkTableWrapperQuery = graphql`
	query TimesheetThisWeeksWorkTableWrapper_Query(
		$pageSize: Int
		$cursor: String
		$taskTypeSearchQuery: TaskSearchQueryType
		$sortValue: String
		$timePeriod: String
		$currentViewingDate: String
		$personId: ID!
	) {
		viewer {
			actualPersonId
			component(name: "timesheet_this_weeks_work_table_wrapper")
			...TimesheetThisWeeksWorkTableWrapper_viewer
				@arguments(
					pageSize: $pageSize
					cursor: $cursor
					taskTypeSearchQuery: $taskTypeSearchQuery
					sortValue: $sortValue
					timePeriod: $timePeriod
					currentViewingDate: $currentViewingDate
					personId: $personId
				)
		}
	}
`;

export {TimesheetThisWeeksWorkTableWrapperQuery};

export default withSocketHandling(
	createPaginationContainer(
		TimesheetThisWeeksWorkTableWrapper,
		{
			viewer: graphql`
				fragment TimesheetThisWeeksWorkTableWrapper_viewer on Viewer
				@argumentDefinitions(
					pageSize: {type: "Int"}
					cursor: {type: "String"}
					taskTypeSearchQuery: {type: "TaskSearchQueryType"}
					sortValue: {type: "String"}
					timePeriod: {type: "String"}
					currentViewingDate: {type: "String"}
					personId: {type: "ID!"}
				) {
					id
					actualPersonId
					harvestUser
					unit4User
					company {
						id
						bambooHREnabled
						hiBobEnabled
						harvestEnabled
						unit4Enabled
						sageIntacctEnabled
						useTimeOffApproval
						roles(includeDisabled: false) {
							edges {
								node {
									id
									name
								}
							}
						}
						taskTypeQueryTasks: allTasks(
							first: $pageSize
							after: $cursor
							searchQuery: $taskTypeSearchQuery
							sortValue: $sortValue
							approvedOnly: true
							runningProjectOnly: true
							simpleResponse: true
						) @connection(key: "Company_taskTypeQueryTasks", filters: []) {
							pageInfo {
								endCursor
								hasNextPage
							}
							edges {
								node {
									id
									userCanDeleteTask
									userCantDeleteTaskReason
									name
									latestUiUpdateAt
									startDay
									startMonth
									startYear
									deadlineYear
									deadlineMonth
									deadlineDay
									timeLeft
									estimateForecastMinutes
									totalMinutesRegistered
									companyTaskId
									favoured
									highPriority
									bug
									blocked
									approved
									canStart
									canBeSetToDone
									billable
									fullAccessToProject
									parentTaskId
									parentTask {
										name
										companyTaskId
									}
									statusColumnV2 {
										id
										name
										category
									}
									assignedPersons {
										id
										firstName
										lastName
										email
										profilePictureId
										profilePictureDefaultId
										permissions
									}
									project {
										id
										name
										companyProjectId
										minutesPerEstimationPoint
										estimationUnit
										projectColor
										projectStartYear
										projectStartMonth
										projectStartDay
										projectEndYear
										projectEndMonth
										projectEndDay
										taskLevels
										status
										fullAccessToProject
										projectPerson(personId: $personId) {
											id
											role {
												id
												name
											}
										}
										harvestProject {
											id
										}
										unit4Project {
											id
											activitiesEnabled
										}
										client {
											id
											name
										}
										# Project Indicator - not fragment because of JS/TS incompatibilty
										name
										companyProjectId
										customProjectId
										projectColor
									}
									phase {
										id
										name
									}
								}
								cursor
							}
						}
					}
				}
			`,
		},
		{
			direction: 'forward',
			getConnectionFromProps({viewer}) {
				return viewer?.company?.taskTypeQueryTasks;
			},
			getFragmentVariables(prevVars, totalCount) {
				return {
					...prevVars,
					pageSize: totalCount,
				};
			},
			getVariables(_, {count, cursor}, fragmentVariables) {
				return {
					...fragmentVariables,
					pageSize: count,
					cursor,
				};
			},
			query: graphql`
				query TimesheetThisWeeksWorkTableWrapperRefetchQuery(
					$pageSize: Int
					$cursor: String
					$taskTypeSearchQuery: TaskSearchQueryType
					$sortValue: String
					$timePeriod: String
					$currentViewingDate: String
					$personId: ID!
				) {
					viewer {
						component(name: "timesheet_this_weeks_work_table_wrapper")
						...TimesheetThisWeeksWorkTableWrapper_viewer
							@arguments(
								pageSize: $pageSize
								cursor: $cursor
								taskTypeSearchQuery: $taskTypeSearchQuery
								sortValue: $sortValue
								timePeriod: $timePeriod
								currentViewingDate: $currentViewingDate
								personId: $personId
							)
					}
				}
			`,
		}
	)
);
